// Order a Test, Part 6 — a plain mouse/touch draw-pad, confirmed explicitly
// with the user: "nothing complicated, no external device integration,
// just a canvas." No signature library — the native Canvas API is simple
// enough here that adding a dependency wasn't worth it.
//
// Exposes its current drawing via a ref: `ref.current.getPngBase64()`
// returns raw base64 (no "data:image/png;base64," prefix — matches what
// the backend's RegisterRequest.signature_png_base64 / ClinicianOrderSubmit
// .patient_signature_png_base64 both expect), or null if nothing was drawn.
// `ref.current.clear()` resets the canvas and the "has drawn" flag.
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Delete } from "@mui/icons-material";

const BRAND = { navy: "#00357a", gold: "#c9a96e" };

function getPointerPos(canvas, e) {
  const rect = canvas.getBoundingClientRect();
  return {
    x: ((e.clientX - rect.left) / rect.width) * canvas.width,
    y: ((e.clientY - rect.top) / rect.height) * canvas.height,
  };
}

const SignaturePad = forwardRef(function SignaturePad({ isDark, height = 160 }, ref) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasDrawnRef = useRef(false);
  const lastPointRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  // 2026-10-08 fix — real bug caught by verification: setting
  // canvas.width/height clears the pixel buffer. This used to run on
  // EVERY isDark change too, so toggling light/dark mode after drawing
  // (e.g. on Register.js, which renders a live ThemeToggle on the same
  // page) silently wiped an already-drawn signature to blank — isEmpty
  // never got reset, so the "Sign here" placeholder never reappeared and
  // getPngBase64() kept returning a valid-looking but blank PNG. Split
  // into two effects: sizing (which wipes, so it must stay isolated from
  // anything that can change mid-draw) runs only on mount/height-change;
  // stroke color updates separately, safely, without touching the buffer.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Render at device-pixel-ratio resolution so the exported PNG (and
    // the embedded signature in the requisition PDF) isn't blurry, while
    // CSS keeps the on-screen size fixed.
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth || canvas.parentElement.clientWidth;
    canvas.width = cssWidth * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = isDark ? "#f1f5f9" : "#0f172a";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d").strokeStyle = isDark ? "#f1f5f9" : "#0f172a";
  }, [isDark]);

  // 2026-10-21 fix (confirmed real gap — the "Unable to preventDefault
  // inside passive event listener invocation" console warning fired on
  // every touch frame): React still registers its delegated touchstart/
  // touchmove listeners as passive by design (kept intentionally even
  // after React 17 moved listeners off `document` — see React core PR
  // #19654), so `e.preventDefault()` inside onTouchStart/onTouchMove
  // always warns regardless of the `touch-action: none` CSS already set
  // below. Pointer Events replace the separate mouse+touch handler pairs
  // with one unified set and don't have this passive-listener conflict.
  // Using setPointerCapture on pointerdown also fixes a second, smaller
  // real bug: the old onMouseLeave handler dropped the stroke mid-drag
  // if the cursor left the canvas — pointer capture keeps receiving
  // move/up events for this pointer even outside the canvas bounds.
  const startDraw = (e) => {
    drawingRef.current = true;
    const canvas = canvasRef.current;
    canvas.setPointerCapture(e.pointerId);
    lastPointRef.current = getPointerPos(canvas, e);
  };

  const draw = (e) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const point = getPointerPos(canvas, e);
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x / dpr, lastPointRef.current.y / dpr);
    ctx.lineTo(point.x / dpr, point.y / dpr);
    ctx.stroke();
    lastPointRef.current = point;
    if (!hasDrawnRef.current) {
      hasDrawnRef.current = true;
      setIsEmpty(false);
    }
  };

  const endDraw = (e) => {
    drawingRef.current = false;
    lastPointRef.current = null;
    const canvas = canvasRef.current;
    if (canvas && e?.pointerId != null && canvas.hasPointerCapture?.(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasDrawnRef.current = false;
    setIsEmpty(true);
  };

  useImperativeHandle(ref, () => ({
    clear,
    isEmpty: () => !hasDrawnRef.current,
    getPngBase64: () => {
      if (!hasDrawnRef.current) return null;
      const dataUrl = canvasRef.current.toDataURL("image/png");
      return dataUrl.replace(/^data:image\/png;base64,/, "");
    },
  }));

  return (
    <Box>
      <Box
        sx={{
          position: "relative", borderRadius: "10px", overflow: "hidden",
          border: `2px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,53,122,0.18)"}`,
          bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#fff",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height, display: "block", touchAction: "none", cursor: "crosshair" }}
          onPointerDown={startDraw} onPointerMove={draw} onPointerUp={endDraw} onPointerCancel={endDraw}
        />
        {isEmpty && (
          <Typography sx={{
            position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, color: isDark ? "rgba(255,255,255,0.25)" : "rgba(15,23,42,0.25)",
            pointerEvents: "none", fontStyle: "italic",
          }}>
            Sign here
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.75 }}>
        <Button
          size="small" onClick={clear} startIcon={<Delete sx={{ fontSize: 15 }} />}
          sx={{ textTransform: "none", fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", "&:hover": { color: BRAND.navy } }}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
});

export default SignaturePad;
